
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { FileText, ClipboardList, Activity, CalendarCheck, Heart, PieChart, TrendingUp, AlertCircle, ArrowRight, Activity as ActivityIcon, ClipboardCheck, Thermometer, PlusCircle, LineChart, BarChart4, HeartPulse, BookOpen } from "lucide-react";

export const JournalSection = () => {
  const isMobile = useIsMobile();
  const navigatePath = isMobile ? "/journal-mobile" : "/journal";
  
  return (
    <div className="grid md:grid-cols-2 gap-12 items-center px-8 max-h-[470px] relative overflow-hidden">
      {/* Transparent background */}
      <div className="absolute inset-0 bg-transparent"></div>
      
      <div className="space-y-6 relative z-10">
        <h2 className="text-3xl font-bold">Journal de santé</h2>
        <p className="text-lg text-muted-foreground">
          Gardez une trace détaillée de votre santé au quotidien
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Suivi quotidien de votre état de santé</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Enregistrement de vos mesures de santé</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Suivi de l'efficacité des traitements</span>
          </div>
        </div>
        <Button asChild>
          <Link to={navigatePath} className="flex items-center gap-2 group">
            Accéder à mon journal
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </div>
      
      <div className="relative z-10 flex justify-center items-center">
        <div className="max-w-[90%] w-full h-[320px] bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl shadow-lg p-6 relative overflow-hidden">
          {/* Journal Interface */}
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-teal-600 to-emerald-600 text-white p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              <h3 className="font-semibold">Journal de santé</h3>
            </div>
            <div className="flex items-center gap-1 text-xs bg-white/20 rounded-full px-2 py-1">
              <CalendarCheck className="h-3.5 w-3.5" />
              <span>Mai 2024</span>
            </div>
          </div>
          
          {/* Journal Entries */}
          <div className="absolute top-[60px] left-3 right-3 overflow-hidden">
            <div className="animate-slide space-y-3">
              <div className="bg-white rounded-lg p-3 shadow-sm flex items-start gap-3 transition-transform hover:translate-x-1 duration-300">
                <div className="bg-emerald-100 text-emerald-600 p-2 rounded-full">
                  <HeartPulse className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex gap-2 items-center">
                    <h4 className="text-sm font-medium">Tension artérielle</h4>
                    <span className="text-xs bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full">Normale</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">120/80 mmHg à 9:30</p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3 text-emerald-500" />
                      <span className="text-xs text-emerald-700">Stable</span>
                    </div>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-400">Aujourd'hui</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-3 shadow-sm flex items-start gap-3 transition-transform hover:translate-x-1 duration-300">
                <div className="bg-blue-100 text-blue-600 p-2 rounded-full">
                  <ActivityIcon className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex gap-2 items-center">
                    <h4 className="text-sm font-medium">Activité physique</h4>
                    <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full">Bon</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">7 852 pas, 45 min d'exercice</p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3 text-blue-500" />
                      <span className="text-xs text-blue-700">+12%</span>
                    </div>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-400">Hier</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-3 shadow-sm flex items-start gap-3 transition-transform hover:translate-x-1 duration-300">
                <div className="bg-amber-100 text-amber-600 p-2 rounded-full">
                  <Thermometer className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex gap-2 items-center">
                    <h4 className="text-sm font-medium">Température</h4>
                    <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full">Normale</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">36.5°C à 18:45</p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <Activity className="h-3 w-3 text-amber-500" />
                      <span className="text-xs text-amber-700">Stable</span>
                    </div>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-400">Hier</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Quick add button */}
          <div className="absolute bottom-4 right-4 bg-teal-500 text-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-teal-600 transition-colors animate-pulse" style={{animationDuration: "2s"}}>
            <PlusCircle className="h-6 w-6" />
          </div>
          
          {/* Analytics preview */}
          <div className="absolute bottom-4 left-4 bg-white/80 backdrop-blur-sm rounded-lg p-2 w-[calc(100%-80px)]">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-700">Résumé de la semaine</span>
              <div className="flex gap-1">
                <LineChart className="h-3 w-3 text-teal-600" />
                <BarChart4 className="h-3 w-3 text-blue-600" />
              </div>
            </div>
            <div className="h-10 flex items-end justify-between gap-1 px-1">
              {[35, 65, 45, 70, 55, 40, 60].map((height, i) => (
                <div key={i} className="w-[8%] relative group">
                  <div 
                    className="absolute bottom-0 w-full rounded-t-sm bg-gradient-to-t from-teal-500 to-teal-400 hover:from-teal-600 hover:to-teal-500 transition-all"
                    style={{height: `${height}%`}}
                  ></div>
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-black text-white text-[8px] px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
                    {height}%
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[8px] text-gray-500">L</span>
              <span className="text-[8px] text-gray-500">M</span>
              <span className="text-[8px] text-gray-500">M</span>
              <span className="text-[8px] text-gray-500">J</span>
              <span className="text-[8px] text-gray-500">V</span>
              <span className="text-[8px] text-gray-500">S</span>
              <span className="text-[8px] text-gray-500">D</span>
            </div>
          </div>
          
          {/* Animated background */}
          <div className="absolute inset-0 top-12">
            <div className="absolute top-10 left-10 w-20 h-20 rounded-full bg-teal-300/10 animate-pulse" style={{animationDuration: "3s"}}></div>
            <div className="absolute bottom-20 right-10 w-16 h-16 rounded-full bg-emerald-300/10 animate-pulse" style={{animationDuration: "4s"}}></div>
          </div>
          
          {/* Animation styles */}
          <style>
            {`
              @keyframes pulse {
                0%, 100% {
                  opacity: 0.5;
                  transform: scale(1);
                }
                50% {
                  opacity: 0.8;
                  transform: scale(1.05);
                }
              }
              
              @keyframes slide {
                0% {
                  transform: translateY(0);
                }
                100% {
                  transform: translateY(-5px);
                }
              }
              
              .animate-slide {
                animation: slide 1s ease-in-out infinite alternate;
              }
            `}
          </style>
        </div>
      </div>
    </div>
  );
};
